import React from 'react';
import { useNavigate } from 'react-router-dom';

/* Fonts declared in index.css via @font-face (local):
   PerfectNineties  → headings
   MaisonNeueBook   → body / ui text                  */

const SERIF = "'PerfectNineties', serif";
const SANS  = "'MaisonNeueBook', sans-serif";

const Arrow = ({ color = '#2d2f24', size = 14 }) => (
  <svg width={size} height={size * 0.72} viewBox="0 0 14 10" fill="none">
    <path d="M1 5h12M8 1l5 4-5 4" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VaiaLogo = ({ height = 42 }) => (
  <img
    src="/vaia-icon.png"
    alt="VAIA"
    style={{ height, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
    onError={(e) => { e.target.src = '/logo.png'; }}
  />
);

export default function HomePage() {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    {
      n: '01',
      title: 'Multi-Asset Portfolio Tracking',
      desc: 'Monitor equities, private markets, real estate, and alternatives — all consolidated in a single real-time view.',
    },
    {
      n: '02',
      title: 'Multi-Currency & Multi-Entity',
      desc: 'Manage wealth across 12+ currencies and multiple family entities with seamless FX and consolidated reporting.',
    },
    {
      n: '03',
      title: 'Institutional-Grade Reporting',
      desc: 'Generate IC packs, portfolio summaries, and tax reports on demand — structured for advisors and principals alike.',
    },
    {
      n: '04',
      title: 'Enterprise Security & Approvals',
      desc: 'Role-based access control, complete audit trails, and a built-in approvals workflow to keep your family office compliant.',
    },
  ];

  return (
    <div style={{ fontFamily: SANS, color: '#2d2f24', backgroundColor: '#fff', overflowX: 'hidden' }}>

      {/* ── Fixed header: banner + nav ── */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>

        {/* Nav */}
        <nav style={{ backgroundColor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
          <div style={{
            maxWidth: 1200, margin: '0 auto', padding: '0 40px',
            height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <VaiaLogo height={42} />

            <button onClick={() => navigate('/login')} style={{
              fontFamily: SANS,
              backgroundColor: '#d4debb', border: '0.5px solid #2a2a2a',
              borderRadius: 50, padding: '7px 18px',
              fontSize: 13, color: '#2d2f24', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Login <Arrow size={12} />
            </button>
          </div>
        </nav>
      </div>

      {/* ── Hero — true full viewport ── */}
      <section style={{
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-end',
      }}>
        <img
          src="/hero-bg.jpg"
          alt=""
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center top',
            pointerEvents: 'none',
          }}
        />
        {/* bottom-to-top dark gradient so text is legible */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)',
          pointerEvents: 'none',
        }} />
        <div style={{ position: 'relative', zIndex: 1, padding: '0 200px 90px' }}>
          <h1 style={{
            fontFamily: SERIF,
            fontSize: 54, color: '#fbfbf3',
            lineHeight: 1.18, fontWeight: 400,
            maxWidth: 480, margin: 0,
          }}>
            From Manual Effort To Intelligent Workflows
          </h1>
        </div>
      </section>

      {/* ── Subtitle + CTA ── */}
      <section style={{ backgroundColor: '#fff', padding: '72px 40px', textAlign: 'center' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#2a2a2a', fontWeight: 400, marginBottom: 20 }}>
          Tech-Powered, Future Ready Solutions
        </h2>
        <p style={{ fontFamily: SANS, fontSize: 16, color: '#2d2f24', maxWidth: 597, margin: '0 auto 36px', lineHeight: 1.75 }}>
          Achieve smarter investment outcomes for your family office with our proprietary domain-trained LLM,
          AI-powered automation, and end-to-end wealth management platform.
        </p>
      </section>

      {/* ── Family Office Platform Features ── */}
      <section id="products" style={{ backgroundColor: 'rgba(234,231,217,0.8)', padding: '80px 40px' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#000', fontWeight: 400, lineHeight: 1.3, margin: 0 }}>
              Our Family Office Platform<br />
              Built For Complexity, Designed For Clarity
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid rgba(0,0,0,0.15)' }}>
            {features.map((f, i) => (
              <div key={i} style={{
                padding: '44px 60px 44px 68px',
                borderRight:  (i === 0 || i === 2) ? '1px solid rgba(0,0,0,0.15)' : 'none',
                borderBottom: (i === 0 || i === 1) ? '1px solid rgba(0,0,0,0.15)' : 'none',
              }}>
                <p style={{ fontFamily: SANS, fontSize: 22, color: 'rgba(102,102,102,0.5)', margin: '0 0 10px' }}>
                  {f.n}
                </p>
                <h3 style={{ fontFamily: SERIF, fontSize: 24, color: 'rgba(0,0,0,0.8)', margin: '0 0 14px', fontWeight: 400 }}>
                  {f.title}
                </h3>
                <p style={{ fontFamily: SANS, fontSize: 16, color: '#666', lineHeight: 1.65, margin: 0 }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform Screenshot ── */}
      <section style={{ backgroundColor: '#f8f6f1', padding: '80px 40px' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#000', fontWeight: 400, marginBottom: 12 }}>
              Your Wealth, One Platform
            </h2>
            <p style={{ fontFamily: SANS, fontSize: 16, color: '#666', maxWidth: 620, margin: '0 auto' }}>
              A unified dashboard that consolidates every asset class, entity, and workflow — so your family office runs with precision, not paperwork.
            </p>
          </div>

          <div style={{ border: '1px solid #ccc', overflow: 'hidden' }}>
            <img
              src="/screenshots/image.png"
              alt="VAIA Family Office Platform"
              style={{ width: '100%', display: 'block' }}
            />
          </div>
        </div>
      </section>

      {/* ── About VAIA ── */}
      <section id="about" style={{ backgroundColor: '#eae7d9', padding: '100px 40px', textAlign: 'center' }}>
        <div style={{ marginBottom: 40, display: 'flex', justifyContent: 'center' }}><VaiaLogo height={46} /></div>
        <p style={{
          fontFamily: SERIF, fontSize: 32, color: '#000',
          maxWidth: 542, margin: '0 auto 48px',
          lineHeight: 1.5, fontWeight: 400, textTransform: 'capitalize',
        }}>
          A Sustainability And Investment Advisory Firm Building Intelligent Tools For Seamless, Insight-Led Decisions.
        </p>
        <button
          onClick={() => window.open('https://vaia.co.in', '_blank', 'noopener noreferrer')}
          style={{
            fontFamily: SANS,
            backgroundColor: '#2d2f24', border: '0.5px solid #2a2a2a',
            borderRadius: 50, padding: '8px 50px',
            fontSize: 16, color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          Learn More About Vaia <Arrow color="#fff" />
        </button>
      </section>

    </div>
  );
}
